import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, setAuthToken } from "@/lib/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [token] = useState(localStorage.getItem("msis_admin_token") || "");
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("overview");
  const [items, setItems] = useState([]);
  const [testIds, setTestIds] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reports, setReports] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    if (!token) { nav("/admin/login"); return; }
    setAuthToken(token);
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => { nav("/admin/login"); });
    api.get("/public/settings").then((r) => setSettings(r.data));
  }, [token, nav]);

  useEffect(() => {
    if (!token) return;
    if (tab === "items") api.get("/admin/items").then((r) => setItems(r.data));
    if (tab === "testids") api.get("/admin/test-ids").then((r) => setTestIds(r.data));
    if (tab === "orders") api.get("/admin/orders").then((r) => setOrders(r.data));
    if (tab === "reports") api.get("/admin/reports").then((r) => setReports(r.data));
    if (tab === "institutions") api.get("/admin/institutions").then((r) => setInstitutions(r.data));
  }, [tab, token]);

  const logout = () => { localStorage.removeItem("msis_admin_token"); setAuthToken(null); nav("/admin/login"); };

  const generateIds = async () => {
    const qty = parseInt(prompt("Number of Test IDs to generate?", "1"));
    if (!qty || qty < 1) return;
    const type = window.confirm("Institutional? (Cancel for Individual)") ? "institution" : "individual";
    const code = type === "institution" ? prompt("Institution code (e.g. INST-XXXXX)?") : null;
    try {
      const r = await api.post("/admin/test-ids/generate", { quantity: qty, type, institution_code: code });
      toast.success(`Generated ${r.data.test_ids.length} Test IDs`);
      api.get("/admin/test-ids").then((r2) => setTestIds(r2.data));
    } catch (e) { toast.error("Failed to generate IDs"); }
  };

  const issueTestIdForOrder = async (order) => {
    if (!window.confirm(`Issue a Test ID for paid order ${order.order_id} (${order.email})?`)) return;
    try {
      const r = await api.post("/admin/orders/issue-test-id", { order_id: order.order_id });
      toast.success(`Issued Test ID: ${r.data.test_ids.join(", ")}. Email sent to buyer.`);
      api.get("/admin/orders").then((r2) => setOrders(r2.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to issue Test ID"); }
  };

  const resendTestIdEmail = async (order) => {
    try {
      await api.post("/admin/orders/resend-email", { order_id: order.order_id });
      toast.success(`Resent Test ID email to ${order.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resend email"); }
  };

  const saveSettings = async () => {
    try {
      await api.patch("/admin/settings", settings);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
  };

  const toggleItem = async (it) => {
    await api.patch(`/admin/items/${it.item_no}`, { active: !it.active });
    api.get("/admin/items").then((r) => setItems(r.data));
  };

  const updateItemText = async (it, text) => {
    await api.patch(`/admin/items/${it.item_no}`, { text });
    toast.success("Updated");
    api.get("/admin/items").then((r) => setItems(r.data));
  };

  if (!overview) return <Layout><div className="container-msis py-20">Loading…</div></Layout>;

  const tabs = [
    ["overview", "Overview"], ["items", "Items"], ["testids", "Test IDs"],
    ["orders", "Orders"], ["reports", "Reports"], ["institutions", "Institutions"],
    ["settings", "Settings"]
  ];

  return (
    <Layout hideFooter>
      <section className="container-msis py-10" data-testid="admin-dashboard">
        <div className="flex flex-wrap justify-between items-end gap-3 border-b border-border pb-6">
          <div>
            <div className="overline">Administration</div>
            <h1 className="font-display font-bold text-3xl tracking-tight mt-1">MSIS Admin</h1>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="admin-logout">Sign out</button>
        </div>

        <div className="mt-8 border-b border-border flex gap-6 overflow-x-auto">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`pb-3 text-sm whitespace-nowrap transition-colors ${tab === k ? "text-navy border-b-2 border-navy font-medium" : "text-slate2"}`}
              data-testid={`admin-tab-${k}`}>{l}</button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "overview" && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border">
              {[
                ["Tests completed", overview.tests_completed],
                ["Tests started", overview.tests_started],
                ["Tests unused", overview.tests_unused],
                ["Orders paid", overview.orders_paid],
                ["Institutions", overview.institutions],
                ["Revenue INR", `₹${overview.revenue_inr.toLocaleString()}`],
                ["Revenue USD", `$${overview.revenue_usd.toLocaleString()}`],
              ].map(([l, v]) => (
                <div key={l} className="bg-white p-5">
                  <div className="overline">{l}</div>
                  <div className="font-display font-bold text-2xl mt-2 tabular-nums">{v}</div>
                </div>
              ))}
            </div>
          )}

          {tab === "items" && (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <button onClick={() => setShowBulkUpdate(true)} className="btn-primary !py-2 !px-4 text-sm" data-testid="bulk-update-btn">
                  + Bulk Update Items
                </button>
                <div className="text-xs text-slate2">Replace all 225 (or any subset) item texts in one go.</div>
              </div>
              {showBulkUpdate && <BulkUpdateModal onClose={() => setShowBulkUpdate(false)} onDone={() => { setShowBulkUpdate(false); api.get("/admin/items").then((r) => setItems(r.data)); }} />}
              <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-items-table">
                <thead className="bg-subtle text-slate2 text-xs uppercase">
                  <tr><th className="text-left p-2 w-12">#</th><th className="text-left p-2 w-20">Domain</th><th className="text-left p-2 w-28">Construct</th><th className="text-left p-2">Item Text</th><th className="text-left p-2 w-20">Active</th></tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.item_no} className="border-t border-border">
                      <td className="p-2 font-mono text-xs">{it.item_no}</td>
                      <td className="p-2 font-mono text-xs">{it.domain_code}</td>
                      <td className="p-2 font-mono text-xs">{it.construct_code}</td>
                      <td className="p-2">
                        <Input defaultValue={it.text} onBlur={(e) => { if (e.target.value !== it.text) updateItemText(it, e.target.value); }}
                          className="text-sm" data-testid={`item-${it.item_no}-text`} />
                      </td>
                      <td className="p-2">
                        <button onClick={() => toggleItem(it)} className={`text-xs px-2 py-1 rounded ${it.active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid={`item-${it.item_no}-toggle`}>
                          {it.active ? "Active" : "Inactive"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}

          {tab === "testids" && (
            <>
              <button onClick={generateIds} className="btn-primary mb-4" data-testid="generate-testids-btn">+ Generate Test IDs</button>
              <div className="border border-border rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-subtle text-slate2 text-xs uppercase">
                    <tr><th className="text-left p-3">Test ID</th><th className="text-left p-3">Type</th><th className="text-left p-3">Institution</th><th className="text-left p-3">Status</th><th className="text-left p-3">Email</th></tr>
                  </thead>
                  <tbody>
                    {testIds.slice(0, 200).map((t) => (
                      <tr key={t.test_id} className="border-t border-border">
                        <td className="p-3 font-mono text-xs">{t.test_id}</td>
                        <td className="p-3 text-xs">{t.type}</td>
                        <td className="p-3 text-xs font-mono">{t.institution_code || '—'}</td>
                        <td className="p-3 text-xs">{t.status}</td>
                        <td className="p-3 text-xs">{t.locked_email || t.purchaser_email || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === "orders" && (
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-subtle text-slate2 text-xs uppercase">
                  <tr><th className="text-left p-3">Order ID</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Qty</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Email</th><th className="text-left p-3">Issued Test IDs</th><th className="text-left p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id} className="border-t border-border">
                      <td className="p-3 font-mono text-xs">{o.order_id}</td>
                      <td className="p-3 text-xs">{o.plan}</td>
                      <td className="p-3 font-mono">{o.quantity}</td>
                      <td className="p-3 font-mono">{o.currency} {(o.amount / 100).toLocaleString()}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-1 rounded ${o.status === 'paid' ? 'bg-green-50 text-green-700' : 'bg-subtle'}`}>{o.status}</span></td>
                      <td className="p-3 text-xs">{o.email}</td>
                      <td className="p-3 text-xs font-mono">
                        {(o.issued_test_ids || []).length > 0
                          ? (o.issued_test_ids || []).map(t => <div key={t} className="select-all">{t}</div>)
                          : <span className="text-slate2">—</span>}
                      </td>
                      <td className="p-3">
                        {o.status === 'paid' && (o.issued_test_ids || []).length > 0 && (
                          <button onClick={() => resendTestIdEmail(o)} className="text-xs text-navy hover:underline" data-testid={`resend-${o.order_id}`}>Resend email</button>
                        )}
                        {o.status === 'paid' && (o.issued_test_ids || []).length === 0 && (
                          <button onClick={() => issueTestIdForOrder(o)} className="text-xs text-orange-600 hover:underline" data-testid={`issue-${o.order_id}`}>Issue Test ID</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "reports" && (
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-subtle text-slate2 text-xs uppercase">
                  <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Test ID</th><th className="text-left p-3">Top Domain</th><th className="text-right p-3">Score</th><th className="text-left p-3">Date</th></tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="p-3">{r.name}<div className="text-xs text-slate2">{r.email}</div></td>
                      <td className="p-3 font-mono text-xs">{r.test_id}</td>
                      <td className="p-3">{r.top_domain}</td>
                      <td className="p-3 text-right font-mono">{(typeof r.top_score === "number" ? r.top_score : 0).toFixed(2)}</td>
                      <td className="p-3 text-xs text-slate2">{(r.created_at || '').split('T')[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "institutions" && (
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-subtle text-slate2 text-xs uppercase">
                  <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Credits</th></tr>
                </thead>
                <tbody>
                  {institutions.map((i) => (
                    <tr key={i.id} className="border-t border-border">
                      <td className="p-3 font-mono text-xs">{i.code}</td>
                      <td className="p-3">{i.name}</td>
                      <td className="p-3 text-xs">{i.email}</td>
                      <td className="p-3 font-mono">{i.credits_used} / {i.credits_purchased}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "settings" && (
            <div className="grid lg:grid-cols-2 gap-8 max-w-5xl" data-testid="admin-settings">
              <div className="space-y-4">
                <div><Label>Timer per item (seconds)</Label><Input type="number" value={settings.timer_seconds || 20} onChange={e => setSettings({ ...settings, timer_seconds: parseInt(e.target.value) })} data-testid="setting-timer" /></div>
                <div><Label>Default response on timeout (1-5)</Label><Input type="number" min={1} max={5} value={settings.default_response || 3} onChange={e => setSettings({ ...settings, default_response: parseInt(e.target.value) })} data-testid="setting-default" /></div>
                <div><Label>Individual price (INR)</Label><Input type="number" value={settings.individual_price_inr || 360} onChange={e => setSettings({ ...settings, individual_price_inr: parseInt(e.target.value) })} data-testid="setting-price-inr" /></div>
                <div><Label>Individual price (USD)</Label><Input type="number" value={settings.individual_price_usd || 9} onChange={e => setSettings({ ...settings, individual_price_usd: parseInt(e.target.value) })} /></div>
                <div><Label>Institutional price (USD)</Label><Input type="number" value={settings.institutional_price_usd || 9} onChange={e => setSettings({ ...settings, institutional_price_usd: parseInt(e.target.value) })} /></div>
                <div><Label>Contact email</Label><Input value={settings.contact_email || ""} onChange={e => setSettings({ ...settings, contact_email: e.target.value })} /></div>
                <div><Label>Contact phone</Label><Input value={settings.contact_phone || ""} onChange={e => setSettings({ ...settings, contact_phone: e.target.value })} placeholder="+91 9703368234" data-testid="setting-contact-phone" /></div>
              </div>
              <div className="space-y-4">
                <div><Label>About MSIS</Label><Textarea rows={5} value={settings.about_msis || ""} onChange={e => setSettings({ ...settings, about_msis: e.target.value })} data-testid="setting-about" /></div>
                <div><Label>Test Instructions</Label><Textarea rows={10} value={settings.instructions || ""} onChange={e => setSettings({ ...settings, instructions: e.target.value })} data-testid="setting-instructions" /></div>
              </div>
              <div className="lg:col-span-2"><button onClick={saveSettings} className="btn-primary" data-testid="save-settings-btn">Save Settings</button></div>

              <div className="lg:col-span-2 border-t border-border pt-8 mt-4">
                <div className="overline mb-2">Institutional INR Pricing Slabs</div>
                <h3 className="font-display font-bold text-xl">Volume Pricing Editor</h3>
                <p className="text-sm text-slate2 mt-1 mb-4">Define quantity-based price tiers for institutional Indian subscribers. Set &quot;Up to&quot; to a very large value (e.g. 999999) for the last &quot;and above&quot; slab.</p>
                <SlabsEditor settings={settings} setSettings={setSettings} onSave={saveSettings} />
              </div>

              <div className="lg:col-span-2 border-t border-border pt-8 mt-4">
                <div className="overline mb-2">Security</div>
                <h3 className="font-display font-bold text-xl">Change Admin Password</h3>
                <p className="text-sm text-slate2 mt-1 mb-4">Use a strong password (min 8 characters). You will be signed out after saving.</p>
                <ChangePasswordForm onChanged={logout} />
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

function ChangePasswordForm({ onChanged }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await api.post("/admin/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed. Please sign in again.");
      setTimeout(() => onChanged && onChanged(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 max-w-3xl" data-testid="change-password-form">
      <div><Label>Current Password</Label><Input type="password" value={cur} onChange={e => setCur(e.target.value)} required data-testid="current-password-input" /></div>
      <div><Label>New Password</Label><Input type="password" value={next} onChange={e => setNext(e.target.value)} required data-testid="new-password-input" /></div>
      <div><Label>Confirm New Password</Label><Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="confirm-password-input" /></div>
      <div className="sm:col-span-3"><button className="btn-primary" disabled={loading} data-testid="change-password-btn">{loading ? "Saving…" : "Change Password"}</button></div>
    </form>
  );
}


function SlabsEditor({ settings, setSettings, onSave }) {
  const slabs = settings.pricing_slabs_inr || [];

  const updateRow = (idx, key, value) => {
    const next = slabs.map((row, i) => {
      if (i !== idx) return row;
      const copy = [...row];
      copy[key] = parseInt(value || "0") || 0;
      return copy;
    });
    setSettings({ ...settings, pricing_slabs_inr: next });
  };

  const addRow = () => {
    const last = slabs[slabs.length - 1] || [1, 100, 360];
    const newLo = (last[1] || 0) + 1;
    setSettings({ ...settings, pricing_slabs_inr: [...slabs, [newLo, newLo + 99, 300]] });
  };

  const removeRow = (idx) => {
    setSettings({ ...settings, pricing_slabs_inr: slabs.filter((_, i) => i !== idx) });
  };

  const validate = () => {
    for (let i = 0; i < slabs.length; i++) {
      const [lo, hi, p] = slabs[i];
      if (lo < 1 || hi < lo || p < 1) return `Row ${i + 1}: invalid values`;
      if (i > 0 && lo !== slabs[i - 1][1] + 1) return `Row ${i + 1}: "From" must equal previous "To" + 1 (continuous slabs)`;
    }
    return null;
  };

  const saveSlabs = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    await onSave();
  };

  return (
    <div data-testid="slabs-editor">
      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-slate2 text-xs uppercase">
            <tr>
              <th className="text-left p-3 w-16">Slab</th>
              <th className="text-left p-3">From (tests)</th>
              <th className="text-left p-3">Up to (tests)</th>
              <th className="text-left p-3">₹ per test</th>
              <th className="text-left p-3 w-24">Display</th>
              <th className="text-right p-3 w-20">Action</th>
            </tr>
          </thead>
          <tbody>
            {slabs.map((row, idx) => {
              const [lo, hi, p] = row;
              const display = hi > 100000 ? `${lo}+ (and above)` : `${lo}–${hi}`;
              return (
                <tr key={idx} className="border-t border-border">
                  <td className="p-3 font-mono text-slate2">{idx + 1}</td>
                  <td className="p-3"><Input type="number" min={1} value={lo} onChange={e => updateRow(idx, 0, e.target.value)} className="h-9" data-testid={`slab-${idx}-from`} /></td>
                  <td className="p-3"><Input type="number" min={1} value={hi} onChange={e => updateRow(idx, 1, e.target.value)} className="h-9" data-testid={`slab-${idx}-to`} /></td>
                  <td className="p-3"><Input type="number" min={1} value={p} onChange={e => updateRow(idx, 2, e.target.value)} className="h-9" data-testid={`slab-${idx}-price`} /></td>
                  <td className="p-3 font-mono text-xs text-slate2">{display}</td>
                  <td className="p-3 text-right">
                    {slabs.length > 1 && (
                      <button onClick={() => removeRow(idx)} className="text-xs text-red-600 hover:underline" data-testid={`slab-${idx}-remove`}>Remove</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-3 mt-4">
        <button onClick={addRow} className="btn-ghost" data-testid="slab-add-btn">+ Add Slab</button>
        <button onClick={saveSlabs} className="btn-primary" data-testid="slab-save-btn">Save Pricing Slabs</button>
      </div>
      <div className="mt-3 text-xs text-slate2">
        <b>Tip:</b> Slabs must be continuous (next slab&apos;s &quot;From&quot; must equal previous slab&apos;s &quot;Up to&quot; + 1). For an open-ended last tier like &quot;501 and above&quot;, set &quot;Up to&quot; to a large value such as <span className="font-mono">999999</span>.
      </div>
    </div>
  );
}


function BulkUpdateModal({ onClose, onDone }) {
  const [mode, setMode] = useState("paste"); // "paste" or "json"
  const [startNo, setStartNo] = useState(1);
  const [text, setText] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildItemsList = () => {
    if (mode === "paste") {
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
      const start = parseInt(startNo) || 1;
      return lines.map((t, i) => ({ item_no: start + i, text: t }));
    }
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) throw new Error("Must be a JSON array");
      return parsed.map(e => ({ item_no: parseInt(e.item_no), text: String(e.text || "").trim() })).filter(e => e.item_no && e.text);
    } catch (e) { toast.error("Invalid JSON: " + e.message); return []; }
  };

  const handlePreview = () => {
    const list = buildItemsList();
    if (list.length === 0) { toast.error("No valid items found."); return; }
    setPreview(list);
  };

  const handleConfirm = async () => {
    if (!preview || preview.length === 0) return;
    setLoading(true);
    try {
      const r = await api.post("/admin/items/bulk-update", { items: preview });
      toast.success(`Updated ${r.data.updated} of ${r.data.total_received} items.`);
      if (r.data.not_found && r.data.not_found.length > 0) {
        toast.warning(`${r.data.not_found.length} item_no values were not found and skipped.`);
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bulk-update-modal">
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-border px-6 py-4 flex justify-between items-center sticky top-0 bg-white">
          <div>
            <div className="overline">Bulk Update</div>
            <h2 className="font-display font-bold text-xl">Replace Item Texts</h2>
          </div>
          <button onClick={onClose} className="text-2xl text-slate2 hover:text-ink" data-testid="bulk-close">×</button>
        </div>
        <div className="p-6 space-y-4">
          {!preview && (
            <>
              <div className="flex gap-2 border-b border-border">
                <button onClick={() => setMode("paste")} className={`pb-2 text-sm ${mode === "paste" ? "border-b-2 border-navy text-navy font-medium" : "text-slate2"}`} data-testid="mode-paste">Paste Items (one per line)</button>
                <button onClick={() => setMode("json")} className={`pb-2 text-sm ${mode === "json" ? "border-b-2 border-navy text-navy font-medium" : "text-slate2"}`} data-testid="mode-json">Advanced (JSON)</button>
              </div>

              {mode === "paste" && (
                <div className="space-y-3">
                  <div className="bg-subtle border border-border p-3 rounded-md text-xs text-slate2">
                    <b>How to use:</b> Open your Excel file → copy <b>Column N</b> (new item texts) → paste below. Each line = one item. Set the starting Item # — usually <b>1</b> if you&apos;re replacing all 225 items in order.
                  </div>
                  <div>
                    <Label>Starting Item #</Label>
                    <Input type="number" min={1} max={225} value={startNo} onChange={e => setStartNo(e.target.value)} className="w-32" data-testid="bulk-start-no" />
                  </div>
                  <div>
                    <Label>Paste new item texts (one item per line)</Label>
                    <Textarea rows={14} value={text} onChange={e => setText(e.target.value)} placeholder="I enjoy interpreting business problems through numbers and calculations.&#10;I feel comfortable using numerical information to compare business alternatives.&#10;..." className="font-mono text-xs" data-testid="bulk-paste" />
                    <div className="text-xs text-slate2 mt-1">Lines detected: <span className="font-mono">{text.split(/\r?\n/).filter(l => l.trim()).length}</span></div>
                  </div>
                </div>
              )}

              {mode === "json" && (
                <div className="space-y-3">
                  <div className="bg-subtle border border-border p-3 rounded-md text-xs text-slate2">
                    <b>JSON format:</b> array of objects with item_no + text. Use this for partial / non-sequential updates.
                  </div>
                  <Textarea rows={14} value={jsonText} onChange={e => setJsonText(e.target.value)} placeholder='[{"item_no":1,"text":"New text..."},{"item_no":2,"text":"Another..."}]' className="font-mono text-xs" data-testid="bulk-json" />
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button onClick={onClose} className="btn-ghost">Cancel</button>
                <button onClick={handlePreview} className="btn-primary" data-testid="bulk-preview-btn">Preview Changes →</button>
              </div>
            </>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-md text-sm text-amber-900">
                <b>Preview:</b> {preview.length} items will be replaced. Verify the first few rows below before confirming.
              </div>
              <div className="border border-border rounded-md max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-subtle sticky top-0">
                    <tr><th className="text-left p-2 w-16">Item #</th><th className="text-left p-2">New Text</th></tr>
                  </thead>
                  <tbody>
                    {preview.map((p, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2 font-mono">{p.item_no}</td>
                        <td className="p-2">{p.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setPreview(null)} className="btn-ghost">← Back to edit</button>
                <button onClick={handleConfirm} disabled={loading} className="btn-primary" data-testid="bulk-confirm-btn">{loading ? "Updating..." : `Confirm & Update ${preview.length} Items`}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
